import { useEffect, useState } from 'react';
import client from '../api/client';
import { messaggioErrore } from '../utils/apiError';

// «Quando in casa…» (01/10/2026): l'evento di Home Assistant che fa partire la
// regola. Gli eventi sono quelli di casa_eventi.py, scelti dall'utente.
const field = 'w-full border rounded-lg px-3 py-2 bg-surface text-sm';

export const EVENTI_CASA = {
  allarme_scattato: { etichetta: 'L’allarme scatta', frase: 'l’allarme scatta', tipi: ['allarme'] },
  allarme: { etichetta: 'L’allarme viene inserito o disinserito', frase: 'l’allarme viene inserito o disinserito', tipi: ['allarme'] },
  porta: { etichetta: 'La porta si apre o si chiude', frase: 'la porta', tipi: ['serratura'] },
  movimento: { etichetta: 'Un sensore rileva movimento', frase: 'un sensore rileva movimento', tipi: ['sensore'] },
  batteria: { etichetta: 'Una batteria scende sotto la soglia', frase: 'una batteria scende sotto la soglia', tipi: ['batteria'] },
  pulsante: { etichetta: 'Premi un pulsante (telecomando Hue)', frase: 'premi', tipi: ['pulsante'] },
  non_risponde: { etichetta: 'Un dispositivo, o Home Assistant, non risponde più', frase: 'qualcosa in casa non risponde più',
    tipi: ['luce', 'allarme', 'serratura', 'sensore', 'batteria'] },
};
export const casaPredefinita = () => ({ evento: 'allarme_scattato', dispositivi: [], solo_se_nessuno: false });

// «Quando la porta si apre, se non c'è nessuno»: per il riepilogo e la riga chiusa.
export function fraseEvento(home = {}) {
  const evento = EVENTI_CASA[home.evento] || EVENTI_CASA.allarme_scattato;
  let frase = evento.frase;
  if (home.evento === 'porta') frase += { aperta: ' si apre', chiusa: ' si chiude' }[home.verso] || ' si apre o si chiude';
  if (home.evento === 'batteria') frase = `una batteria scende sotto il ${home.soglia ?? 20}%`;
  if (home.evento === 'pulsante') {
    frase = `${home.dispositivi?.length === 1 ? 'premi il pulsante scelto' : 'premi un pulsante'}${{ lunga: ' a lungo', qualsiasi: ' (breve o lungo)' }[home.pressione] || ''}`;
    if (home.dispositivi?.length > 1) frase += ` (${home.dispositivi.length} pulsanti scelti)`;
    return `Quando ${frase}`;
  }
  if (home.dispositivi?.length) frase += ` (${home.dispositivi.length === 1 ? '1 dispositivo scelto' : `${home.dispositivi.length} dispositivi scelti`})`;
  if (home.solo_se_nessuno) frase += ', se non c’è nessuno in casa';
  return `Quando ${frase}`;
}

export default function CasaEventoFields({ home, onChange, collegata = true }) {
  const [elenco, setElenco] = useState(null);
  const [errore, setErrore] = useState('');
  const evento = EVENTI_CASA[home.evento] || EVENTI_CASA.allarme_scattato;
  const set = patch => onChange({ ...home, ...patch });
  useEffect(() => {
    if (!collegata) return;
    client.get('/v1/home-assistant/dispositivi/', { timeout: 30000 }).then(({ data }) => setElenco(data.dispositivi))
      .catch(e => setErrore(messaggioErrore(e, 'Non riesco a leggere i dispositivi di casa.')));
  }, [collegata]);
  if (!collegata) {
    return <p className="text-sm text-warning">Home Assistant non è ancora collegato: aggiungi indirizzo e codice nel menu Servizi,
      alla voce «Casa · Home Assistant».</p>;
  }
  const adatti = (elenco || []).filter(d => evento.tipi.includes(d.tipo));
  const scelti = home.dispositivi || [];
  const cambia = id => set({ dispositivi: scelti.includes(id) ? scelti.filter(x => x !== id) : [...scelti, id] });
  return <div className="space-y-3">
    <label className="block text-sm">Cosa deve succedere
      <select className={field} value={home.evento} onChange={e => set({ evento: e.target.value, dispositivi: [],
        // «Solo se non c'è nessuno» vale per porta e movimento: nascosta, non deve restare accesa.
        solo_se_nessuno: ['porta', 'movimento'].includes(e.target.value) && !!home.solo_se_nessuno,
        ...(e.target.value === 'porta' ? { verso: home.verso || 'entrambe' } : {}),
        ...(e.target.value === 'batteria' ? { soglia: home.soglia ?? 20 } : {}),
        ...(e.target.value === 'pulsante' ? { pressione: home.pressione || 'breve' } : {}) })}>
        {Object.entries(EVENTI_CASA).map(([id, e]) => <option key={id} value={id}>{e.etichetta}</option>)}
      </select>
    </label>
    {home.evento === 'porta' && <fieldset className="space-y-1">
      <legend className="text-sm">Quando</legend>
      {[['entrambe', 'Si apre o si chiude'], ['aperta', 'Si apre'], ['chiusa', 'Si chiude']].map(([valore, etichetta]) =>
        <label key={valore} className="flex min-h-[36px] items-center gap-2 text-sm">
          <input type="radio" name="verso-porta" checked={(home.verso || 'entrambe') === valore} onChange={() => set({ verso: valore })} />{etichetta}
        </label>)}
    </fieldset>}
    {home.evento === 'pulsante' && <fieldset className="space-y-1">
      <legend className="text-sm">Come lo premi</legend>
      {[['breve', 'Pressione breve'], ['lunga', 'Pressione lunga'], ['qualsiasi', 'Tutte e due']].map(([valore, etichetta]) =>
        <label key={valore} className="flex min-h-[36px] items-center gap-2 text-sm">
          <input type="radio" name="pressione" checked={(home.pressione || 'breve') === valore} onChange={() => set({ pressione: valore })} />{etichetta}
        </label>)}
      <p className="text-xs text-muted">Scegli un pulsante solo: tutti insieme farebbero partire la regola con qualsiasi tasto.</p>
    </fieldset>}
    {home.evento === 'batteria' && <label className="block text-sm">Sotto quale percentuale
      <input type="number" min="5" max="80" className={field} value={home.soglia ?? 20}
        onChange={e => set({ soglia: Number(e.target.value) })} />
    </label>}
    <fieldset className="space-y-1">
      <legend className="text-sm">Quali dispositivi {scelti.length ? '' : <span className="text-muted">(nessuno scelto: tutti)</span>}</legend>
      {!elenco && !errore && <p className="text-sm text-muted">Chiedo a casa…</p>}
      {elenco && !adatti.length && <p className="text-sm text-muted">In casa non ci sono dispositivi di questo tipo.</p>}
      {adatti.map(d => <label key={d.id} className="flex min-h-[36px] items-center gap-2 text-sm">
        <input type="checkbox" checked={scelti.includes(d.id)} onChange={() => cambia(d.id)} />
        <span className="min-w-0 break-words">{d.nome} <span className="text-muted">· {d.stato}</span></span>
      </label>)}
      {home.evento === 'non_risponde' && !scelti.length && <p className="text-xs text-muted">Con tutti i dispositivi avvisa anche se
        Home Assistant stesso non risponde per più di cinque minuti, e quando torna.</p>}
    </fieldset>
    {['porta', 'movimento'].includes(home.evento) && <label className="flex min-h-[36px] items-center gap-2 text-sm">
      <input type="checkbox" checked={!!home.solo_se_nessuno} onChange={e => set({ solo_se_nessuno: e.target.checked })} />
      Solo se non c’è nessuno in casa
    </label>}
    {errore && <p role="alert" className="text-sm text-warning">{errore}</p>}
  </div>;
}
